import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppShell } from "./components/shell/AppShell";
import { landingPath } from "./lib/dashboard";
import { useSession } from "./lib/session";
import { Account } from "./pages/Account";
import { Alerts } from "./pages/Alerts";
import { AppDetail } from "./pages/AppDetail";
import { Apps } from "./pages/Apps";
import { Audit } from "./pages/Audit";
import { ChargePointDetail } from "./pages/ChargePointDetail";
import { ConnectionAttempts } from "./pages/ConnectionAttempts";
import { Customers } from "./pages/Customers";
import { Dashboard } from "./pages/Dashboard";
import { Depots } from "./pages/Depots";
import { Events } from "./pages/Events";
import { Fleet } from "./pages/Fleet";
import { HolderDetail } from "./pages/HolderDetail";
import { Holders } from "./pages/Holders";
import { Identities } from "./pages/Identities";
import { IdentityDetail } from "./pages/IdentityDetail";
import { Login } from "./pages/Login";
import { Logs } from "./pages/Logs";
import { NotFound } from "./pages/NotFound";
import { Operators } from "./pages/Operators";
import { PaymentProviders } from "./pages/PaymentProviders";
import { Policies } from "./pages/Policies";
import { Prices } from "./pages/Prices";
import { PublicOrders } from "./pages/PublicOrders";
import { Roles } from "./pages/Roles";
import { SessionPushes } from "./pages/SessionPushes";
import { Sessions } from "./pages/Sessions";
import { SetPassword } from "./pages/SetPassword";
import { Settings } from "./pages/Settings";
import { RequireAuth } from "./routes/RequireAuth";

/**
 * The route table.
 *
 * Flat and explicit rather than lazily code-split: the whole panel is a handful
 * of pages against a shared component set, and at this size splitting would add
 * a loading state to every navigation to save a bundle that is already inside the
 * app budget.
 *
 * Every authenticated route sits under one `RequireAuth` so there is no way to
 * add a page and forget to guard it.
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Unguarded like the login page: whoever opens a sign-in link has no
          session yet, and for an invitation has never had one. */}
      <Route path="/set-password" element={<SetPassword />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Landing />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/fleet/:chargePointId" element={<ChargePointDetail />} />
        <Route path="/connections" element={<ConnectionAttempts />} />
        <Route path="/depots" element={<Depots />} />
        <Route path="/prices" element={<Prices />} />
        <Route path="/holders" element={<Holders />} />
        <Route path="/holders/:holderId" element={<HolderDetail />} />
        <Route path="/identities" element={<Identities />} />
        <Route path="/identities/:identityId" element={<IdentityDetail />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/sessions" element={<Sessions />} />
        {/* The session page is now a panel over the list. Old links — bookmarks,
            tickets, exported reports — still land on the same session. */}
        <Route path="/sessions/:sessionId" element={<SessionRedirect />} />
        <Route path="/public-orders" element={<PublicOrders />} />
        {/* Retired into a panel over the list, like sessions; old links still land. */}
        <Route path="/public-orders/:orderId" element={<PublicOrderRedirect />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/events" element={<Events />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/operators" element={<Operators />} />
        <Route path="/roles" element={<Roles />} />
        <Route path="/payment-providers" element={<PaymentProviders />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/session-pushes" element={<SessionPushes />} />
        <Route path="/apps/:appId" element={<AppDetail />} />
        <Route path="/account" element={<Account />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/**
 * `/`: the overview for anybody it has a panel for, the fleet for everybody else —
 * which is where everyone landed before there was an overview.
 */
function Landing() {
  const { can } = useSession();
  return <Navigate to={landingPath(can)} replace />;
}

function SessionRedirect() {
  const { sessionId = "" } = useParams();
  return <Navigate to={`/sessions?session=${encodeURIComponent(sessionId)}`} replace />;
}

function PublicOrderRedirect() {
  const { orderId = "" } = useParams();
  return <Navigate to={`/public-orders?order=${encodeURIComponent(orderId)}`} replace />;
}
